import type { PartiesBreakdown, PartiesBySource, PartyLink } from '../../types';
import { ExpandTable, PartyCellList } from './common';
import { NOT_AVAILABLE } from '../utils';

type Source = keyof PartiesBreakdown;

type PartyTableRow = Record<Source, PartyLink[]> & {
  label: string;
  emptyLabel?: string;
};

const SOURCES: { key: Source; header: string }[] = [
  { key: 'vendor', header: 'Vendor' },
  { key: 'autoprovision', header: 'Autoprovision' },
  { key: 'manual', header: 'Manual' },
];

/**
 * The expanded card's Parties table, from the card payload: Conveyor/Conveyee
 * rows, plus an Other row when a role names neither side. Each row says what an
 * empty cell shows; the Other row leaves it to the dash.
 * @param {object} props The table.
 * @param {PartiesBreakdown} props.breakdown The parties by source.
 * @return {JSX.Element} The table.
 */
export function PartiesTable({ breakdown }: { breakdown: PartiesBreakdown }) {
  /**
   * One role row across the source columns.
   * @param {string} label The row label.
   * @param {keyof PartiesBySource} side The role side.
   * @param {string} [emptyLabel] What an empty cell shows.
   * @return {PartyTableRow} The row.
   */
  const partyRow = (
    label: string,
    side: keyof PartiesBySource,
    emptyLabel?: string
  ): PartyTableRow => ({
    label,
    emptyLabel,
    vendor: breakdown.vendor[side],
    autoprovision: breakdown.autoprovision[side],
    manual: breakdown.manual[side],
  });
  const rows = [
    partyRow('Conveyor', 'grantors', NOT_AVAILABLE),
    partyRow('Conveyee', 'grantees', NOT_AVAILABLE),
  ];
  if (SOURCES.some(({ key }) => breakdown[key].others.length > 0)) {
    rows.push(partyRow('Other', 'others'));
  }

  return (
    <ExpandTable<PartyTableRow>
      bordered
      columns={[
        { header: 'Parties', cell: (row) => row.label },
        ...SOURCES.map(({ key, header }) => ({
          header,
          cell: (row: PartyTableRow) => (
            <PartyCellList parties={row[key]} emptyLabel={row.emptyLabel} />
          ),
        })),
      ]}
      rows={rows}
      rowKey={(row) => row.label}
    />
  );
}
